// Google Sheets Data Service for Dynamic Quiz Content
// Published CSV URL for Quiz Data

const SHEET_CSV_URL = "https://docs.google.com/spreadsheets/d/e/2PACX-1vSHqCDN_pAwmv6LUjapHG5UNmhOz1FpAiYwNSkjIuG6QU6L2XRanW4EbccU4m3xzMQPmAB36r8xp-v9/pub?gid=113099526&single=true&output=csv";

/**
 * Parse a tab-separated row into an object
 */
const parseQuestionRow = (row) => {
  const parts = row.split('\t').map(p => p.trim());
  if (parts.length < 15) return null;
  
  return {
    id: parseInt(parts[0]) || 0,
    kosha: parts[1] || '',
    koshaName: parts[2] || '',
    koshaDescription: parts[3] || '',
    question: parts[4] || '',
    options: [
      { value: parseInt(parts[5]) || 1, label: parts[6] || '' },
      { value: parseInt(parts[7]) || 2, label: parts[8] || '' },
      { value: parseInt(parts[9]) || 3, label: parts[10] || '' },
      { value: parseInt(parts[11]) || 4, label: parts[12] || '' },
      { value: parseInt(parts[13]) || 5, label: parts[14] || '' },
    ]
  };
};

/**
 * Parse kosha info row
 */
const parseKoshaInfoRow = (row) => {
  const parts = row.split('\t').map(p => p.trim());
  if (parts.length < 6) return null;
  
  return {
    key: parts[0] || '',
    name: parts[1] || '',
    subtitle: parts[2] || '',
    description: parts[3] || '',
    color: parts[4] || '#000000',
    icon: parts[5] || '🔮'
  };
};

/**
 * Parse insight row
 */
const parseInsightRow = (row) => {
  const parts = row.split('\t').map(p => p.trim());
  if (parts.length < 5) return null;
  
  return {
    key: parts[0] || '',
    highMessage: parts[1] || '',
    highQuote: parts[2] || '',
    lowMessage: parts[3] || '',
    lowQuote: parts[4] || ''
  };
};

/**
 * Fetch and parse quiz data from Google Sheets
 * Returns { relationshipQuestions, lifeQuestions, relationshipKoshaInfo, lifeKoshaInfo, relationshipInsights, lifeInsights }
 */
export const fetchQuizDataFromSheet = async () => {
  try {
    const response = await fetch(SHEET_CSV_URL);
    if (!response.ok) {
      throw new Error('Failed to fetch quiz data');
    }
    
    const csvText = await response.text();
    const lines = csvText.split('\n').slice(2); // Skip header rows
    
    const relationshipQuestions = [];
    const lifeQuestions = [];
    const relationshipKoshaInfo = {};
    const lifeKoshaInfo = {};
    const relationshipInsights = {};
    const lifeInsights = {};
    
    for (const line of lines) {
      if (!line.trim()) continue;
      
      // Split by comma but respect quoted fields
      const columns = line.split('","').map(c => c.replace(/^"|"$/g, ''));
      
      // Column 0: Relationship Questions
      if (columns[0]) {
        const q = parseQuestionRow(columns[0]);
        if (q && q.id > 0) relationshipQuestions.push(q);
      }
      
      // Column 1: Life Questions
      if (columns[1]) {
        const q = parseQuestionRow(columns[1]);
        if (q && q.id > 0) lifeQuestions.push(q);
      }
      
      // Column 2: Relationship Kosha Info
      if (columns[2]) {
        const info = parseKoshaInfoRow(columns[2]);
        if (info && info.key) relationshipKoshaInfo[info.key] = info;
      }
      
      // Column 3: Life Kosha Info
      if (columns[3]) {
        const info = parseKoshaInfoRow(columns[3]);
        if (info && info.key) lifeKoshaInfo[info.key] = info;
      }
      
      // Column 4: Relationship Insights
      if (columns[4]) {
        const insight = parseInsightRow(columns[4]);
        if (insight && insight.key) relationshipInsights[insight.key] = insight;
      }
      
      // Column 5: Life Insights
      if (columns[5]) {
        const insight = parseInsightRow(columns[5]);
        if (insight && insight.key) lifeInsights[insight.key] = insight;
      }
    }
    
    return {
      relationshipQuestions,
      lifeQuestions,
      relationshipKoshaInfo,
      lifeKoshaInfo,
      relationshipInsights,
      lifeInsights,
      success: true
    };
  } catch (error) {
    console.error('Error fetching quiz data from Google Sheets:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Calculate quiz results
 */
export const calculateResultsFromAnswers = (answers, questions) => {
  const koshaScores = {
    annamaya: [],
    pranamaya: [],
    manomaya: [],
    vijnanamaya: [],
    anandamaya: []
  };

  questions.forEach(q => {
    if (answers[q.id] && koshaScores[q.kosha]) {
      koshaScores[q.kosha].push(answers[q.id]);
    }
  });

  const calculatePercentage = (scores) => {
    if (scores.length === 0) return 0;
    const avg = scores.reduce((a, b) => a + b, 0) / scores.length;
    return Math.round((avg / 5) * 100);
  };

  const annamaya = calculatePercentage(koshaScores.annamaya);
  const pranamaya = calculatePercentage(koshaScores.pranamaya);
  const manomaya = calculatePercentage(koshaScores.manomaya);
  const vijnanamaya = calculatePercentage(koshaScores.vijnanamaya);
  const anandamaya = calculatePercentage(koshaScores.anandamaya);

  const overall = Math.round((annamaya + pranamaya + manomaya + vijnanamaya + anandamaya) / 5);

  return { annamaya, pranamaya, manomaya, vijnanamaya, anandamaya, overall };
};

/**
 * Get insight based on score
 */
export const getInsightFromData = (kosha, score, insights) => {
  const insight = insights[kosha];
  if (!insight) {
    return {
      type: score >= 70 ? 'strength' : 'opportunity',
      message: score >= 70 ? 'This area is a strength.' : 'This area has room for growth.',
      quote: 'Continue your journey of self-discovery.'
    };
  }
  
  const isStrength = score >= 70;
  return {
    type: isStrength ? 'strength' : 'opportunity',
    message: isStrength ? insight.highMessage : insight.lowMessage,
    quote: isStrength ? insight.highQuote : insight.lowQuote
  };
};

export default fetchQuizDataFromSheet;
